import { useBrand } from "hooks/useBrand";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Paths from "routes/Paths";
import { getSignInPathWithRedirect, getSignUpPathWithRedirect } from "routes/signInRedirect";
import { useSession } from "Session";

import NotificationBell from "./NotificationBell";
import UserMenu from "./UserMenu";
import ViewIcon from "./ViewIcon";

import styles from "./TopNav.module.css";

/**
 * Sticky site header — brand, primary navigation, search and session actions.
 * Shared by the tree view and settings pages.
 */
const TopNav = () => {
	const { t } = useTranslation("homePage");
	const { brand } = useBrand();
	const navigate = useNavigate();
	const location = useLocation();
	const { hasSession } = useSession();
	// A signed-out visitor reading a public tree should come back to it after signing in, not to the homepage.
	const currentPath = `${location.pathname}${location.search}`;

	const goHome = useCallback((): void => navigate(Paths.root), [navigate]);
	const goToExplore = useCallback((): void => navigate(Paths.explore), [navigate]);
	const goToSignIn = useCallback(
		(): void => navigate(getSignInPathWithRedirect(currentPath)),
		[currentPath, navigate]
	);
	const goToSignUp = useCallback(
		(): void => navigate(getSignUpPathWithRedirect(currentPath)),
		[currentPath, navigate]
	);

	return (
		<header className={styles.topNav}>
			<button className={styles.brand} onClick={goHome} type="button">
				<img alt="" className={styles.brandLogo} src={`${process.env.PUBLIC_URL}/assets/images/logo.png`} />
				<span className={styles.brandWordmark}>{brand}</span>
			</button>

			{/* "My Trees" and the search box are account features; a signed-out reader gets neither. */}
			{hasSession && (
				<nav aria-label={t("view.primaryNav")} className={styles.navLinks}>
					<button className={styles.navLink} onClick={goHome} type="button">
						{t("navigation.myTrees")}
					</button>
					<button className={styles.navLink} onClick={goToExplore} type="button">
						{t("navigation.explore")}
					</button>
				</nav>
			)}

			<div className={styles.topNavActions}>
				{hasSession ? (
					<>
						<span className={styles.topSearch}>
							<ViewIcon name="search" />
							<input
								aria-label={t("toolbar.searchLabel")}
								placeholder={t("toolbar.searchPlaceholder")}
								readOnly
							/>
						</span>
						<NotificationBell />
						<UserMenu />
					</>
				) : (
					<>
						<button className={styles.navLink} onClick={goToSignIn} type="button">
							{t("view.signIn")}
						</button>
						<button className={styles.signInButton} onClick={goToSignUp} type="button">
							{t("view.signUp")}
						</button>
					</>
				)}
			</div>
		</header>
	);
};

export default TopNav;
